import React from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useLanguage } from '../../../contexts/LanguageContext';
import useCreditCardPayment from './hooks/useCreditCardPayment';
import PaymentAmountSection from './components/PaymentAmountSection';
import CreditCardForm from './components/CreditCardForm';
import InstallmentOptions from './components/InstallmentOptions';

const CreditCardCollectionPage = () => {
    const { th } = useLanguage();

    const {
        // State
        formData,
        errors,
        submitted,
        installmentOptions,
        showInstallments,
        cardInfo,
        maskedCardNumber,
        monthOptions,
        yearOptions,

        // Loading states
        isLoadingInstallments,
        isProcessingPayment,
        isValidatingCard,

        // Actions
        updateField,
        handleCardNumberChange,
        selectInstallment,
        handlePayment,
        resetForm
    } = useCreditCardPayment();

    // Ödeme tamamlama onayı
    const confirmPayment = () => {
        confirmDialog({
            message: th('creditCardCollection.confirmPayment.message') || 'Ödeme işlemini tamamlamak istediğinize emin misiniz?',
            header: th('creditCardCollection.confirmPayment.header') || 'Ödeme Onayı',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: th('common.yes') || 'Evet',
            rejectLabel: th('common.no') || 'Hayır',
            accept: handlePayment,
            acceptClassName: 'p-button-success'
        });
    };

    // Form temizleme onayı
    const confirmReset = () => {
        confirmDialog({
            message: th('creditCardCollection.confirmReset.message') || 'Tüm form bilgileri silinecektir. Devam etmek istiyor musunuz?',
            header: th('creditCardCollection.confirmReset.header') || 'Formu Temizle',
            icon: 'pi pi-info-circle',
            acceptLabel: th('common.yes') || 'Evet',
            rejectLabel: th('common.no') || 'Hayır',
            accept: resetForm,
            acceptClassName: 'p-button-danger'
        });
    };

    return (
        <div className="card">
            <ConfirmDialog />

            {/* Sayfa Başlığı - Card içinde */}
            <h1 className="m-0 mb-4">{th('creditCardCollection.title') || 'Kredi Kartı ile Tahsilat'}</h1>

            {/* Tutar ve Para Birimi */}
            <PaymentAmountSection
                amount={formData.amount}
                currency={formData.currency}
                onAmountChange={(value) => updateField('amount', value)}
                onCurrencyChange={(value) => updateField('currency', value)}
                errors={errors}
                submitted={submitted}
                disabled={isProcessingPayment}
            />

            <Divider />

            {/* Kart Bilgileri */}
            <CreditCardForm
                cardNumber={formData.cardNumber}
                cardHolderName={formData.cardHolderName}
                expiryMonth={formData.expiryMonth}
                expiryYear={formData.expiryYear}
                cvv={formData.cvv}
                maskedCardNumber={maskedCardNumber}
                cardInfo={cardInfo}
                monthOptions={monthOptions}
                yearOptions={yearOptions}
                onCardNumberChange={handleCardNumberChange}
                onFieldChange={updateField}
                errors={errors}
                submitted={submitted}
                disabled={isProcessingPayment}
                isValidatingCard={isValidatingCard}
            />

            <Divider />

            {/* Taksit Seçenekleri */}
            <InstallmentOptions
                installmentOptions={installmentOptions}
                selectedInstallment={formData.selectedInstallment}
                onSelectInstallment={selectInstallment}
                showInstallments={showInstallments}
                isLoading={isLoadingInstallments}
                disabled={isProcessingPayment}
                amount={formData.amount}
                currency={formData.currency}
            />

            <Divider />

            {/* Butonlar */}
            <div className="flex justify-content-start gap-3 pt-3">
                <Button
                    label={th('creditCardCollection.buttons.clearForm') || 'Formu Temizle'}
                    icon="pi pi-refresh"
                    className="p-button-outlined p-button-secondary"
                    onClick={confirmReset}
                    disabled={isProcessingPayment}
                />
                <Button
                    label={th('creditCardCollection.buttons.completePayment') || 'Ödemeyi Tamamla'}
                    icon="pi pi-check"
                    className="p-button-success"
                    onClick={confirmPayment}
                    loading={isProcessingPayment}
                    disabled={!formData.selectedInstallment || isLoadingInstallments}
                />
            </div>
        </div>
    );
};

export default CreditCardCollectionPage;
